import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useAuth } from "../App";
import Navbar from "../components/Navbar";
import ImageUpload from "../components/ImageUpload";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { 
  Save, Plus, Trash2, Calendar as CalendarIcon, MapPin, 
  Phone, Mail, Globe, Music, Clock, Guitar, Edit,
  Search, Store, ChevronLeft, ChevronRight, Ticket, Loader2, Check,
  CreditCard, Crown, Lock
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MUSIC_GENRES = [
  { category: "Rock", genres: ["rock", "rock-clasico", "rock-alternativo", "rock-indie", "hard-rock", "rock-progresivo", "rock-psicodelico", "glam-rock", "art-rock"] },
  { category: "Punk", genres: ["punk", "punk-rock", "post-punk", "hardcore-punk", "pop-punk", "skate-punk", "street-punk", "oi", "crust-punk", "punk-77"] },
  { category: "Metal Clásico", genres: ["metal", "heavy-metal", "thrash-metal", "power-metal", "doom-metal", "speed-metal", "gothic-metal"] },
  { category: "Metal Moderno", genres: ["nu-metal", "metalcore", "progressive-metal", "symphonic-metal", "folk-metal", "industrial-metal", "groove-metal", "djent"] },
  { category: "Metal Extremo", genres: ["death-metal", "black-metal", "deathcore", "grindcore", "brutal-death", "melodic-death", "slam"] },
  { category: "Otros", genres: ["grunge", "stoner", "sludge", "post-rock", "noise-rock", "math-rock", "emo", "screamo", "shoegaze", "blues-rock", "rockabilly", "psychobilly"] }
];

const EVENT_TAGS = [
  { id: "concierto", label: "Concierto", icon: "🎸" },
  { id: "festival", label: "Festival", icon: "🎪" },
  { id: "acustico", label: "Acústico", icon: "🎵" },
  { id: "jam-session", label: "Jam Session", icon: "🥁" },
  { id: "open-mic", label: "Open Mic", icon: "🎤" },
  { id: "tributo", label: "Tributo", icon: "⭐" },
  { id: "release-party", label: "Release Party", icon: "💿" }
];

export default function BandDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [band, setBand] = useState(null);
  const [events, setEvents] = useState([]);
  const [bars, setBars] = useState([]);
  const [cities, setCities] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedBar, setSelectedBar] = useState(null);
  const [searchingBars, setSearchingBars] = useState(false);
  const [processingRequest, setProcessingRequest] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [planFeatures, setPlanFeatures] = useState(null);

  // Band form state
  const [bandForm, setBandForm] = useState({
    name: "",
    description: "",
    city_id: "",
    phone: "",
    email: "",
    website: "",
    image_url: "",
    music_genres: [],
    available_dates: []
  });

  // Event form state
  const [eventForm, setEventForm] = useState({
    title: "",
    description: "",
    event_date: null,
    event_time: "21:00",
    end_time: "",
    image_url: "",
    price: "",
    ticket_url: "",
    ticket_info: "",
    tags: [],
    music_genres: []
  });

  // Bar search filters
  const [barSearchCity, setBarSearchCity] = useState("");
  const [barSearchGenres, setBarSearchGenres] = useState([]);
  const [newCityName, setNewCityName] = useState("");
  const [creatingCity, setCreatingCity] = useState(false);
  const [customVenueName, setCustomVenueName] = useState("");
  const [useCustomVenue, setUseCustomVenue] = useState(false);

  // Calendar state
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);

  const getAuthHeaders = () => {
    const currentToken = token || localStorage.getItem("token");
    return { Authorization: `Bearer ${currentToken}` };
  };

  useEffect(() => {
    if (user && user.user_type !== "band") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user]);

  // Check subscription status after Stripe redirect
  useEffect(() => {
    const subscription = searchParams.get("subscription");
    const sessionId = searchParams.get("session_id");
    
    if (subscription === "success" && sessionId) {
      const verifyPayment = async () => {
        try {
          const res = await axios.get(`${API}/band-subscription/status/${sessionId}`, { headers: getAuthHeaders() });
          if (res.data.payment_status === "paid") {
            toast.success("¡SUSCRIPCIÓN STAGE ACTIVADA!");
            fetchData();
          } else {
            toast.info("PAGO PENDIENTE DE CONFIRMACIÓN");
          }
        } catch (e) {
          console.error("Error verifying payment:", e);
        }
      };
      verifyPayment();
      // Clean URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (subscription === "cancelled") {
      toast.info("SUSCRIPCIÓN CANCELADA");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [searchParams]);

  // Helper function to check if a feature is enabled
  const hasFeature = (featureName) => {
    if (!planFeatures?.features) return true; // Default to true if not loaded
    return planFeatures.features[featureName] === true;
  };

  const fetchData = async () => {
    try {
      const [bandRes, eventsRes, citiesRes, requestsRes, featuresRes] = await Promise.all([
        axios.get(`${API}/bands/me`, { headers: getAuthHeaders() }),
        axios.get(`${API}/bands/dashboard/my-events`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cities`),
        axios.get(`${API}/bands/dashboard/requests`, { headers: getAuthHeaders() }),
        axios.get(`${API}/bands/me/features`, { headers: getAuthHeaders() })
      ]);

      setBand(bandRes.data);
      setEvents(eventsRes.data);
      setCities(citiesRes.data);
      setRequests(requestsRes.data);
      setPlanFeatures(featuresRes.data);

      setBandForm({
        name: bandRes.data.name || "",
        description: bandRes.data.description || "",
        city_id: bandRes.data.city_id || "",
        phone: bandRes.data.phone || "",
        email: bandRes.data.email || "",
        website: bandRes.data.website || "",
        image_url: bandRes.data.image_url || "",
        music_genres: bandRes.data.music_genres || [],
        available_dates: bandRes.data.available_dates || []
      });

      // Parse available dates for calendar
      if (bandRes.data.available_dates) {
        setSelectedDates(bandRes.data.available_dates.map(d => new Date(d)));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403 || error.response?.status === 404) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Convert selected dates to ISO strings
      const availableDates = selectedDates.map(d => format(d, "yyyy-MM-dd"));
      
      // Build data explicitly
      const data = {
        name: bandForm.name,
        description: bandForm.description,
        city: bandForm.city,
        music_genres: bandForm.music_genres || [],
        members_count: bandForm.members_count,
        formation_year: bandForm.formation_year,
        website: bandForm.website,
        social_links: bandForm.social_links,
        contact_email: bandForm.contact_email,
        contact_phone: bandForm.contact_phone,
        available_dates: availableDates
      };
      
      // Always include image_url if it has a value
      if (bandForm.image_url) {
        data.image_url = bandForm.image_url;
      }
      
      await axios.put(`${API}/bands/me`, data, { headers: getAuthHeaders() });
      
      toast.success("PERFIL ACTUALIZADO");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL GUARDAR");
    } finally {
      setSaving(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    setProcessingRequest(requestId);
    try {
      await axios.post(`${API}/bands/dashboard/requests/${requestId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("SOLICITUD APROBADA - EVENTO CREADO");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL APROBAR");
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!confirm("¿Estás seguro de rechazar esta solicitud?")) return;
    setProcessingRequest(requestId);
    try {
      await axios.post(`${API}/bands/dashboard/requests/${requestId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("SOLICITUD RECHAZADA");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL RECHAZAR");
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleSearchBars = async () => {
    setSearchingBars(true);
    try {
      const params = new URLSearchParams();
      if (barSearchCity) params.append("city_id", barSearchCity);
      if (barSearchGenres.length > 0) params.append("genres", barSearchGenres.join(","));
      
      const res = await axios.get(`${API}/bands/dashboard/bars?${params.toString()}`, { headers: getAuthHeaders() });
      setBars(res.data);
      
      if (res.data.length === 0) {
        toast.info("NO SE ENCONTRARON BARES");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL BUSCAR BARES");
    } finally {
      setSearchingBars(false);
    }
  };

  const handleOpenEventDialog = (bar) => {
    setSelectedBar(bar);
    setEventForm({
      title: "",
      description: "",
      event_date: null,
      event_time: "21:00",
      end_time: "",
      image_url: "",
      price: "",
      ticket_url: "",
      ticket_info: "",
      tags: [],
      music_genres: bandForm.music_genres || []
    });
    setEventDialogOpen(true);
  };

  const handleCreateCity = async () => {
    if (!newCityName.trim()) {
      toast.error("ESCRIBE EL NOMBRE DE LA CIUDAD");
      return;
    }
    setCreatingCity(true);
    try {
      const res = await axios.post(`${API}/cities`, { name: newCityName.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("CIUDAD CREADA");
      setCities([...cities, res.data]);
      setNewCityName("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR CIUDAD");
    } finally {
      setCreatingCity(false);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    // Validate based on whether using custom venue or selected bar
    if (!eventForm.title || !eventForm.event_date) {
      toast.error("COMPLETA LOS CAMPOS OBLIGATORIOS");
      return;
    }
    
    if (!useCustomVenue && !selectedBar) {
      toast.error("SELECCIONA UN BAR O AÑADE UNA UBICACIÓN PERSONALIZADA");
      return;
    }
    
    if (useCustomVenue && !customVenueName.trim()) {
      toast.error("ESCRIBE EL NOMBRE DEL LOCAL");
      return;
    }

    try {
      const [hours, minutes] = eventForm.event_time.split(":");
      const eventDate = new Date(eventForm.event_date);
      eventDate.setHours(parseInt(hours), parseInt(minutes));

      let endDate = null;
      if (eventForm.end_time) {
        const [endHours, endMinutes] = eventForm.end_time.split(":");
        endDate = new Date(eventForm.event_date);
        endDate.setHours(parseInt(endHours), parseInt(endMinutes));
      }

      const eventData = {
        title: eventForm.title,
        description: eventForm.description,
        event_date: eventDate.toISOString(),
        end_date: endDate?.toISOString(),
        image_url: eventForm.image_url,
        price: eventForm.price,
        ticket_url: eventForm.ticket_url,
        ticket_info: eventForm.ticket_info,
        tags: eventForm.tags,
        music_genres: eventForm.music_genres
      };
      
      if (useCustomVenue) {
        eventData.custom_venue = customVenueName.trim();
      } else {
        eventData.bar_id = selectedBar.bar_id;
      }

      const res = await axios.post(`${API}/bands/dashboard/events`, eventData, { headers: getAuthHeaders() });

      toast.success(res.data.message);
      setEventDialogOpen(false);
      setCustomVenueName("");
      setUseCustomVenue(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL CREAR EVENTO");
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("¿ELIMINAR ESTE EVENTO?")) return;

    try {
      await axios.delete(`${API}/bands/dashboard/events/${eventId}`, { headers: getAuthHeaders() });
      toast.success("EVENTO ELIMINADO");
      fetchData();
    } catch (error) {
      toast.error("ERROR AL ELIMINAR");
    }
  };

  const toggleDateSelection = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    const exists = selectedDates.some(d => format(d, "yyyy-MM-dd") === dateStr);
    
    if (exists) {
      setSelectedDates(selectedDates.filter(d => format(d, "yyyy-MM-dd") !== dateStr));
    } else {
      setSelectedDates([...selectedDates, date]);
    }
  };

  const isDateSelected = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return selectedDates.some(d => format(d, "yyyy-MM-dd") === dateStr);
  };

  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.event_date);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    const startPadding = (firstDay.getDay() + 6) % 7;
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-[#111] w-1/3" />
            <div className="h-64 bg-[#111]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tighter mb-8">
          PANEL <span className="text-primary">BANDA</span>
        </h1>

        <Tabs defaultValue="profile" className="space-y-6">
          <TabsList className="bg-[#111] border border-zinc-800 rounded-none p-1 flex-wrap">
            {requests.filter(r => r.status === "pending").length > 0 && (
              <TabsTrigger value="requests" className="font-mono text-sm rounded-none data-[state=active]:bg-yellow-500 data-[state=active]:text-black relative">
                <Mail className="w-4 h-4 mr-1" />
                SOLICITUDES
                <span className="absolute -top-1 -right-1 bg-yellow-500 text-black text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {requests.filter(r => r.status === "pending").length}
                </span>
              </TabsTrigger>
            )}
            <TabsTrigger value="profile" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              PERFIL
            </TabsTrigger>
            <TabsTrigger value="calendar" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <CalendarIcon className="w-4 h-4 mr-1" />
              DISPONIBILIDAD
            </TabsTrigger>
            <TabsTrigger value="bars" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Store className="w-4 h-4 mr-1" />
              BUSCAR BARES
            </TabsTrigger>
            <TabsTrigger value="events" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white">
              <Music className="w-4 h-4 mr-1" />
              MIS EVENTOS ({events.length})
            </TabsTrigger>
            <TabsTrigger value="subscription" className="font-mono text-sm rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-subscription">
              <Crown className="w-4 h-4 mr-1" />
              PLAN
            </TabsTrigger>
          </TabsList>

          {/* Requests Tab */}
          <TabsContent value="requests">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <h2 className="text-xl font-black text-white mb-6 flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                SOLICITUDES DE BARES
              </h2>
              
              <p className="text-zinc-500 text-sm mb-6">
                Los bares pueden enviarte propuestas de conciertos. Puedes aprobarlas o rechazarlas.
              </p>

              {requests.filter(r => r.status === "pending").length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO HAY SOLICITUDES PENDIENTES</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requests.filter(r => r.status === "pending").map((req) => (
                    <div key={req.request_id} className="bg-[#0a0a0a] border border-zinc-800 p-6" data-testid={`request-${req.request_id}`}>
                      <div className="flex flex-col lg:flex-row justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Store className="w-5 h-5 text-primary" />
                            <span className="text-lg font-bold text-white">{req.bar_name}</span>
                            {req.bar_city && (
                              <span className="text-xs text-zinc-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" /> {req.bar_city}
                              </span>
                            )}
                          </div>
                          
                          <h3 className="text-xl font-bold text-white mb-2">{req.title}</h3>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-zinc-400 mb-3">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4 text-primary" />
                              {format(new Date(req.proposed_date), "dd MMM yyyy 'a las' HH:mm", { locale: es })}
                            </span>
                            {req.proposed_price && (
                              <span className="flex items-center gap-1">
                                <Ticket className="w-4 h-4 text-green-500" />
                                {req.proposed_price}
                              </span>
                            )}
                          </div>
                          
                          {req.message && (
                            <p className="text-zinc-500 text-sm italic bg-zinc-900 p-3 border-l-2 border-primary">
                              "{req.message}"
                            </p>
                          )}
                          
                          <p className="text-zinc-600 text-xs mt-2">
                            Recibido: {format(new Date(req.created_at), "dd/MM/yyyy HH:mm", { locale: es })}
                          </p>
                        </div>
                        
                        <div className="flex lg:flex-col gap-2">
                          <Button
                            onClick={() => handleApproveRequest(req.request_id)}
                            disabled={processingRequest === req.request_id}
                            className="bg-green-600 hover:bg-green-700 text-white font-mono flex-1"
                            data-testid={`approve-${req.request_id}`}
                          >
                            {processingRequest === req.request_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-2" />
                                ACEPTAR
                              </>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleRejectRequest(req.request_id)}
                            disabled={processingRequest === req.request_id}
                            variant="outline"
                            className="border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-mono flex-1"
                            data-testid={`reject-${req.request_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            RECHAZAR
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* History of processed requests */}
              {requests.filter(r => r.status !== "pending").length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-bold text-zinc-500 mb-4">HISTORIAL</h3>
                  <div className="space-y-2">
                    {requests.filter(r => r.status !== "pending").map((req) => (
                      <div key={req.request_id} className="bg-zinc-900/50 border border-zinc-800/50 p-4 opacity-60">
                        <div className="flex justify-between items-center">
                          <div>
                            <span className="text-white font-bold">{req.title}</span>
                            <span className="text-zinc-500 text-sm ml-2">- {req.bar_name}</span>
                          </div>
                          <span className={`text-xs font-mono px-2 py-1 ${
                            req.status === "approved" ? "bg-green-500/20 text-green-500" : "bg-red-500/20 text-red-500"
                          }`}>
                            {req.status === "approved" ? "APROBADA" : "RECHAZADA"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <h2 className="text-xl font-black text-white mb-6">PERFIL DE LA BANDA</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">NOMBRE DE LA BANDA *</Label>
                  <Input
                    value={bandForm.name}
                    onChange={(e) => setBandForm({ ...bandForm, name: e.target.value })}
                    className="bg-transparent border-b-2 border-zinc-700 rounded-none focus:border-primary"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">CIUDAD</Label>
                  <Select value={bandForm.city_id} onValueChange={(v) => setBandForm({ ...bandForm, city_id: v })}>
                    <SelectTrigger className="bg-transparent border-b-2 border-zinc-700 rounded-none">
                      <SelectValue placeholder="Selecciona ciudad" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-zinc-800 rounded-none max-h-60">
                      {cities.map((city) => (
                        <SelectItem key={city.city_id} value={city.city_id} className="font-mono text-sm">
                          {city.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2 mt-2">
                    <Input 
                      value={newCityName} 
                      onChange={(e) => setNewCityName(e.target.value)} 
                      className="bg-transparent border-b border-zinc-700 rounded-none text-sm" 
                      placeholder="Nueva ciudad..."
                    />
                    <Button 
                      type="button"
                      onClick={handleCreateCity} 
                      disabled={creatingCity}
                      size="sm"
                      className="bg-zinc-700 hover:bg-zinc-600 text-white font-mono text-xs"
                    >
                      {creatingCity ? "..." : "+"}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                <Textarea
                  value={bandForm.description}
                  onChange={(e) => setBandForm({ ...bandForm, description: e.target.value })}
                  className="bg-transparent border-2 border-zinc-700 rounded-none min-h-[100px]"
                  placeholder="Describe tu banda, estilo, influencias..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Phone className="inline w-3 h-3 mr-1" />
                    TELÉFONO
                  </Label>
                  <Input
                    value={bandForm.phone}
                    onChange={(e) => setBandForm({ ...bandForm, phone: e.target.value })}
                    className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Mail className="inline w-3 h-3 mr-1" />
                    EMAIL
                  </Label>
                  <Input
                    type="email"
                    value={bandForm.email}
                    onChange={(e) => setBandForm({ ...bandForm, email: e.target.value })}
                    className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                    <Globe className="inline w-3 h-3 mr-1" />
                    WEB / REDES SOCIALES
                  </Label>
                  <Input
                    value={bandForm.website}
                    onChange={(e) => setBandForm({ ...bandForm, website: e.target.value })}
                    className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                    placeholder="https://..."
                  />
                </div>
              </div>

              {/* Music Genres */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <h3 className="text-lg font-black text-white mb-4 flex items-center gap-2">
                  <Guitar className="w-5 h-5 text-primary" />
                  GÉNEROS MUSICALES
                </h3>
                <div className="space-y-3 max-h-48 overflow-y-auto bg-[#0a0a0a] p-4 border border-zinc-800">
                  {MUSIC_GENRES.map((category) => (
                    <div key={category.category}>
                      <p className="font-mono text-[10px] text-primary mb-1">{category.category.toUpperCase()}</p>
                      <div className="flex flex-wrap gap-1">
                        {category.genres.map((genre) => (
                          <button
                            key={genre}
                            type="button"
                            onClick={() => {
                              const newGenres = bandForm.music_genres.includes(genre)
                                ? bandForm.music_genres.filter(g => g !== genre)
                                : [...bandForm.music_genres, genre];
                              setBandForm({ ...bandForm, music_genres: newGenres });
                            }}
                            className={`px-2 py-1 font-mono text-[10px] border transition-all ${
                              bandForm.music_genres.includes(genre)
                                ? "bg-primary border-primary text-white"
                                : "bg-transparent border-zinc-700 text-zinc-500 hover:border-primary"
                            }`}
                          >
                            {genre.replace(/-/g, " ").toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Upload */}
              <div className="mt-6">
                <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN DE LA BANDA</Label>
                <ImageUpload
                  value={bandForm.image_url}
                  onChange={(url) => setBandForm({ ...bandForm, image_url: url })}
                  token={token}
                />
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={saving}
                className="w-full mt-6 btn-ruido bg-primary text-white font-mono"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                GUARDAR PERFIL
              </Button>
            </div>
          </TabsContent>

          {/* Calendar/Availability Tab */}
          <TabsContent value="calendar">
            <div className="bg-[#111] border border-zinc-800 p-6">
              {!hasFeature("calendar_management") && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="text-yellow-500 font-mono text-sm font-bold">FUNCIÓN PREMIUM</p>
                      <p className="text-zinc-400 text-xs">La gestión de calendario está disponible con el plan Stage. Puedes ver tus fechas, pero no modificarlas.</p>
                    </div>
                    <Button
                      onClick={() => document.querySelector('[data-testid="tab-subscription"]')?.click()}
                      size="sm"
                      className="bg-yellow-500 hover:bg-yellow-600 text-black font-mono ml-auto"
                    >
                      UPGRADE
                    </Button>
                  </div>
                </div>
              )}
              <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                <CalendarIcon className="w-5 h-5 text-primary" />
                DISPONIBILIDAD
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Selecciona las fechas en las que estás disponible para tocar. Los bares podrán buscarte por estas fechas.
              </p>

              <div className="flex items-center justify-between mb-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1))}
                  className="text-zinc-400 hover:text-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <span className="font-mono text-sm text-white">
                  {format(calendarMonth, "MMMM yyyy", { locale: es }).toUpperCase()}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1))}
                  className="text-zinc-400 hover:text-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {["LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB", "DOM"].map(day => (
                  <div key={day} className="text-center font-mono text-xs text-zinc-500 py-2">
                    {day}
                  </div>
                ))}
                
                {getCalendarDays().map((date, index) => {
                  if (!date) {
                    return <div key={`empty-${index}`} className="aspect-square" />;
                  }
                  
                  const isToday = date.toDateString() === new Date().toDateString();
                  const isSelected = isDateSelected(date);
                  const dayEvents = getEventsForDate(date);
                  const isPast = date < new Date(new Date().setHours(0,0,0,0));
                  
                  return (
                    <button
                      key={date.toISOString()}
                      onClick={() => !isPast && hasFeature("calendar_management") && toggleDateSelection(date)}
                      disabled={isPast || !hasFeature("calendar_management")}
                      className={`aspect-square border p-1 transition-all ${
                        isPast || !hasFeature("calendar_management")
                          ? "border-zinc-900 bg-zinc-900/50 cursor-not-allowed" 
                          : isSelected 
                            ? "border-primary bg-primary/20 hover:bg-primary/30" 
                            : "border-zinc-800 bg-[#0a0a0a] hover:border-zinc-600"
                      } ${isToday ? "ring-1 ring-primary" : ""}`}
                    >
                      <div className={`text-xs font-mono ${
                        isPast ? "text-zinc-700" : isSelected ? "text-primary" : "text-zinc-400"
                      }`}>
                        {date.getDate()}
                      </div>
                      {isSelected && !isPast && (
                        <Check className="w-3 h-3 text-primary mx-auto mt-0.5" />
                      )}
                      {dayEvents.length > 0 && (
                        <div className="text-[8px] font-mono bg-zinc-700 text-white px-1 mt-0.5 truncate">
                          {dayEvents.length} evento
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 pt-6 border-t border-zinc-800">
                <p className="font-mono text-sm text-zinc-400 mb-4">
                  FECHAS SELECCIONADAS: {selectedDates.length}
                </p>
                {selectedDates.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedDates
                      .sort((a, b) => a - b)
                      .slice(0, 10)
                      .map(date => (
                        <span 
                          key={date.toISOString()} 
                          className="bg-primary/20 text-primary font-mono text-xs px-2 py-1"
                        >
                          {format(date, "d MMM", { locale: es })}
                        </span>
                      ))}
                    {selectedDates.length > 10 && (
                      <span className="text-zinc-500 font-mono text-xs py-1">
                        +{selectedDates.length - 10} más
                      </span>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={saving || !hasFeature("calendar_management")}
                className="w-full mt-6 btn-ruido bg-primary text-white font-mono disabled:opacity-50"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {hasFeature("calendar_management") ? "GUARDAR DISPONIBILIDAD" : "UPGRADE PARA GUARDAR"}
              </Button>
            </div>
          </TabsContent>

          {/* Search Bars Tab */}
          <TabsContent value="bars">
            <div className="bg-[#111] border border-zinc-800 p-6">
              {!hasFeature("propose_concerts") && (
                <div className="bg-blue-500/10 border border-blue-500/30 p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-blue-400 font-mono text-sm font-bold">MODO VISUALIZACIÓN</p>
                      <p className="text-zinc-400 text-xs">Puedes buscar bares pero para proponer conciertos necesitas el plan Stage.</p>
                    </div>
                  </div>
                </div>
              )}
              <h2 className="text-xl font-black text-white mb-2 flex items-center gap-2">
                <Store className="w-5 h-5 text-primary" />
                BUSCAR BARES
              </h2>
              <p className="text-zinc-500 text-sm mb-6">
                Encuentra bares donde tocar {hasFeature("propose_concerts") ? "y propón conciertos" : ""}
              </p>

              {/* Search Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">CIUDAD</Label>
                  <Select value={barSearchCity || "all"} onValueChange={(v) => setBarSearchCity(v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-transparent border-b-2 border-zinc-700 rounded-none">
                      <SelectValue placeholder="Todas las ciudades" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#111] border-zinc-800 rounded-none">
                      <SelectItem value="all" className="font-mono text-sm">TODAS</SelectItem>
                      {cities.map((city) => (
                        <SelectItem key={city.city_id} value={city.city_id} className="font-mono text-sm">
                          {city.name.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-mono text-xs text-zinc-500 mb-2 block">GÉNEROS</Label>
                  <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto bg-[#0a0a0a] p-2 border border-zinc-800">
                    {MUSIC_GENRES.flatMap(cat => cat.genres).slice(0, 12).map((genre) => (
                      <button
                        key={genre}
                        type="button"
                        onClick={() => {
                          const newGenres = barSearchGenres.includes(genre)
                            ? barSearchGenres.filter(g => g !== genre)
                            : [...barSearchGenres, genre];
                          setBarSearchGenres(newGenres);
                        }}
                        className={`px-2 py-1 font-mono text-[10px] border transition-all ${
                          barSearchGenres.includes(genre)
                            ? "bg-primary border-primary text-white"
                            : "bg-transparent border-zinc-700 text-zinc-500 hover:border-primary"
                        }`}
                      >
                        {genre.replace(/-/g, " ").toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                onClick={handleSearchBars}
                disabled={searchingBars}
                className="btn-ruido bg-primary text-white font-mono mb-6"
              >
                {searchingBars ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />BUSCANDO...</>
                ) : (
                  <><Search className="w-4 h-4 mr-2" />BUSCAR BARES</>
                )}
              </Button>

              {/* Results */}
              {bars.length > 0 ? (
                <div className="space-y-3">
                  <p className="font-mono text-sm text-zinc-400">{bars.length} BAR(ES) ENCONTRADO(S)</p>
                  {bars.map((bar) => (
                    <div
                      key={bar.bar_id}
                      className="bg-[#0a0a0a] border border-zinc-800 p-4"
                    >
                      <div className="flex items-start gap-4">
                        {bar.image_url ? (
                          <img src={bar.image_url} alt={bar.name} className="w-20 h-20 object-cover" />
                        ) : (
                          <div className="w-20 h-20 bg-zinc-800 flex items-center justify-center">
                            <Store className="w-8 h-8 text-zinc-600" />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-white">{bar.name}</h3>
                          {bar.address && (
                            <p className="text-zinc-500 text-sm flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {bar.address}
                            </p>
                          )}
                          {bar.city_name && (
                            <p className="font-mono text-xs text-zinc-600">{bar.city_name}</p>
                          )}
                          {bar.music_genres?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {bar.music_genres.slice(0, 4).map((genre) => (
                                <span key={genre} className="bg-zinc-800 text-zinc-400 text-[10px] font-mono px-1.5 py-0.5">
                                  {genre.replace(/-/g, " ").toUpperCase()}
                                </span>
                              ))}
                            </div>
                          )}
                          <p className="font-mono text-xs text-primary mt-2">
                            {bar.upcoming_events || 0} eventos próximos
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          {hasFeature("propose_concerts") ? (
                            <Button
                              onClick={() => handleOpenEventDialog(bar)}
                              className="btn-ruido bg-primary text-white font-mono text-xs"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              CREAR EVENTO
                            </Button>
                          ) : (
                            <Button
                              onClick={() => document.querySelector('[data-testid="tab-subscription"]')?.click()}
                              variant="outline"
                              className="border-yellow-500/50 text-yellow-500 font-mono text-xs"
                            >
                              <Lock className="w-4 h-4 mr-1" />
                              UPGRADE
                            </Button>
                          )}
                          {bar.email && (
                            <a
                              href={`mailto:${bar.email}`}
                              className="text-center text-primary font-mono text-xs hover:underline"
                            >
                              <Mail className="w-3 h-3 inline mr-1" />
                              CONTACTAR
                            </a>
                          )}
                        </div>
                      </div>
                      
                      {/* Bar Availability Info */}
                      {(bar.available_dates?.length > 0 || bar.available_hours || bar.booking_conditions) && (
                        <div className="mt-4 pt-4 border-t border-zinc-800">
                          <p className="font-mono text-xs text-green-500 mb-2">
                            <Check className="w-3 h-3 inline mr-1" />
                            DISPONIBILIDAD PARA BANDAS
                          </p>
                          {bar.available_dates?.length > 0 && (
                            <div className="mb-2">
                              <span className="font-mono text-xs text-zinc-500">Fechas: </span>
                              <span className="font-mono text-xs text-zinc-300">
                                {bar.available_dates.slice(0, 5).map(d => format(new Date(d), "d MMM", { locale: es })).join(", ")}
                                {bar.available_dates.length > 5 && ` +${bar.available_dates.length - 5} más`}
                              </span>
                            </div>
                          )}
                          {bar.available_hours && (
                            <div className="mb-2">
                              <span className="font-mono text-xs text-zinc-500">Horarios: </span>
                              <span className="font-mono text-xs text-zinc-300">{bar.available_hours}</span>
                            </div>
                          )}
                          {bar.booking_conditions && (
                            <div>
                              <span className="font-mono text-xs text-zinc-500">Condiciones: </span>
                              <span className="text-xs text-zinc-400">{bar.booking_conditions.substring(0, 100)}{bar.booking_conditions.length > 100 ? "..." : ""}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 border border-zinc-800 bg-[#0a0a0a]">
                  <Store className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">USA LOS FILTROS PARA BUSCAR BARES</p>
                </div>
              )}

              {/* Create Event Dialog */}
              <Dialog open={eventDialogOpen} onOpenChange={setEventDialogOpen}>
                <DialogContent className="bg-[#111] border-zinc-800 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-xl font-black text-white">
                      {useCustomVenue ? "CREAR EVENTO" : `CREAR EVENTO EN ${selectedBar?.name?.toUpperCase() || "BAR"}`}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateEvent} className="space-y-4">
                    {/* Venue Selection */}
                    <div className="bg-zinc-900/50 border border-zinc-700 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-mono text-xs text-zinc-500">UBICACIÓN</Label>
                        <button
                          type="button"
                          onClick={() => setUseCustomVenue(!useCustomVenue)}
                          className="text-primary font-mono text-xs hover:underline"
                        >
                          {useCustomVenue ? "SELECCIONAR BAR" : "NO ENCUENTRO EL LOCAL"}
                        </button>
                      </div>
                      {useCustomVenue ? (
                        <Input
                          value={customVenueName}
                          onChange={(e) => setCustomVenueName(e.target.value)}
                          className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                          placeholder="Nombre del local / sala..."
                          required
                        />
                      ) : (
                        <p className="text-white font-mono text-sm">
                          {selectedBar?.name || "Selecciona un bar de la lista"}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">TÍTULO *</Label>
                      <Input
                        value={eventForm.title}
                        onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                        className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                        placeholder="Nombre del evento"
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">DESCRIPCIÓN</Label>
                      <Textarea
                        value={eventForm.description}
                        onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                        className="bg-transparent border-2 border-zinc-700 rounded-none min-h-[80px]"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">FECHA *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button variant="outline" className="w-full justify-start bg-transparent border-b-2 border-zinc-700 rounded-none font-mono text-sm">
                              <CalendarIcon className="w-4 h-4 mr-2" />
                              {eventForm.event_date ? format(eventForm.event_date, "d MMM yyyy", { locale: es }) : "Seleccionar"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 bg-[#111] border-zinc-800 rounded-none">
                            <Calendar
                              mode="single"
                              selected={eventForm.event_date}
                              onSelect={(date) => setEventForm({ ...eventForm, event_date: date })}
                              locale={es}
                              disabled={(date) => date < new Date()}
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">HORA *</Label>
                        <Input
                          type="time"
                          value={eventForm.event_time}
                          onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                          className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">PRECIO</Label>
                        <Input
                          value={eventForm.price}
                          onChange={(e) => setEventForm({ ...eventForm, price: e.target.value })}
                          className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                          placeholder="Gratis / 10€"
                        />
                      </div>
                      <div>
                        <Label className="font-mono text-xs text-zinc-500 mb-2 block">
                          <Ticket className="inline w-3 h-3 mr-1" />
                          LINK ENTRADAS
                        </Label>
                        <Input
                          value={eventForm.ticket_url}
                          onChange={(e) => setEventForm({ ...eventForm, ticket_url: e.target.value })}
                          className="bg-transparent border-b-2 border-zinc-700 rounded-none"
                          placeholder="https://..."
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">IMAGEN DEL EVENTO</Label>
                      <ImageUpload
                        value={eventForm.image_url}
                        onChange={(url) => setEventForm({ ...eventForm, image_url: url })}
                        token={token}
                      />
                    </div>
                    <div>
                      <Label className="font-mono text-xs text-zinc-500 mb-2 block">TIPO DE EVENTO</Label>
                      <div className="flex flex-wrap gap-1">
                        {EVENT_TAGS.map((tag) => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => {
                              const newTags = eventForm.tags.includes(tag.id)
                                ? eventForm.tags.filter(t => t !== tag.id)
                                : [...eventForm.tags, tag.id];
                              setEventForm({ ...eventForm, tags: newTags });
                            }}
                            className={`px-2 py-1 font-mono text-xs border transition-all ${
                              eventForm.tags.includes(tag.id)
                                ? "bg-primary border-primary text-white"
                                : "bg-transparent border-zinc-700 text-zinc-500 hover:border-primary"
                            }`}
                          >
                            {tag.icon} {tag.label.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    </div>
                    <Button type="submit" className="w-full btn-ruido bg-primary text-white font-mono">
                      CREAR EVENTO
                    </Button>
                    <p className="text-zinc-500 text-xs text-center font-mono">
                      El evento quedará pendiente de aprobación por el bar
                    </p>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>

          {/* My Events Tab */}
          <TabsContent value="events">
            <div className="bg-[#111] border border-zinc-800 p-6">
              <h2 className="text-xl font-black text-white mb-6 flex items-center gap-2">
                <Music className="w-5 h-5 text-primary" />
                MIS EVENTOS
              </h2>

              {events.length > 0 ? (
                <div className="space-y-3">
                  {events.map((event) => {
                    const isPast = new Date(event.event_date) < new Date();
                    return (
                      <div
                        key={event.event_id}
                        className={`bg-[#0a0a0a] border p-4 ${
                          isPast ? "border-zinc-900 opacity-50" : "border-zinc-800"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 flex flex-col items-center justify-center ${
                              isPast ? "bg-zinc-700" : "bg-primary"
                            }`}>
                              <span className="text-lg font-black text-white">
                                {format(new Date(event.event_date), "d")}
                              </span>
                              <span className="text-[10px] font-mono text-white/80 uppercase">
                                {format(new Date(event.event_date), "MMM", { locale: es })}
                              </span>
                            </div>
                            <div>
                              <h3 className={`font-bold ${isPast ? "text-zinc-500" : "text-white"}`}>
                                {event.title}
                              </h3>
                              <p className="font-mono text-xs text-zinc-500">
                                {event.bar_name} · {format(new Date(event.event_date), "HH:mm")}H
                              </p>
                              {event.bar_address && (
                                <p className="text-zinc-600 text-xs flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  {event.bar_address}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                <span className={`font-mono text-[10px] px-2 py-0.5 ${
                                  event.status === "pending" 
                                    ? "bg-yellow-500/20 text-yellow-500" 
                                    : event.status === "approved"
                                      ? "bg-green-500/20 text-green-500"
                                      : "bg-zinc-700 text-zinc-400"
                                }`}>
                                  {event.status === "pending" ? "PENDIENTE" : 
                                   event.status === "approved" ? "APROBADO" : event.status?.toUpperCase()}
                                </span>
                                {isPast && (
                                  <span className="bg-zinc-800 text-zinc-500 text-[10px] font-mono px-2 py-0.5">
                                    PASADO
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteEvent(event.event_id)}
                            className="text-zinc-500 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 border border-zinc-800 bg-[#0a0a0a]">
                  <Music className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
                  <p className="text-zinc-500 font-mono text-sm">NO TIENES EVENTOS CREADOS</p>
                  <p className="text-zinc-600 text-xs mt-2">Busca bares y crea tu primer evento</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Subscription Tab */}
          <TabsContent value="subscription">
            <div className="space-y-6">
              <div className="bg-[#111] border border-zinc-800 p-6">
                <h2 className="text-2xl font-black text-white mb-2">TU PLAN ACTUAL</h2>
                <div className="flex items-center gap-3 mb-6">
                  <span className={`px-4 py-2 font-mono text-sm ${
                    band?.subscription_plan === "pro" 
                      ? "bg-primary text-white" 
                      : "bg-zinc-800 text-zinc-300"
                  }`}>
                    {band?.subscription_plan === "pro" ? "STAGE" : "GARAGE"}
                  </span>
                  {band?.subscription_plan === "pro" && band?.subscription_start && (
                    <span className="text-zinc-500 font-mono text-sm">
                      Activo desde {format(new Date(band.subscription_start), "d MMM yyyy", { locale: es })}
                    </span>
                  )}
                </div>
                <p className="text-zinc-400 text-sm">
                  {band?.subscription_plan === "pro" 
                    ? "Disfruta de todas las funcionalidades premium para bandas."
                    : "Plan básico gratuito. Actualiza para desbloquear más funciones."
                  }
                </p>
              </div>

              {/* Plans */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Free Plan */}
                <div className={`bg-[#111] border-2 p-6 ${
                  band?.subscription_plan === "free" || !band?.subscription_plan ? "border-primary" : "border-zinc-800"
                }`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-black text-white">GARAGE</h3>
                    <h3 className="text-xl font-black text-zinc-500">(Gratis)</h3>
                    {(band?.subscription_plan === "free" || !band?.subscription_plan) && (
                      <span className="bg-primary text-white text-xs font-mono px-2 py-1">ACTUAL</span>
                    )}
                  </div>
                  <p className="text-4xl font-black text-white mb-4">0€<span className="text-sm text-zinc-500 font-normal">/mes</span></p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Perfil de banda completo
                    </li>
                    <li className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Recibir solicitudes de bares
                    </li>
                    <li className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Buscar bares por ciudad y género
                    </li>
                    <li className="flex items-center gap-2 text-zinc-500 text-sm">
                      <Lock className="w-4 h-4 text-zinc-600" />
                      <span className="line-through">Proponer conciertos a bares</span>
                    </li>
                    <li className="flex items-center gap-2 text-zinc-500 text-sm">
                      <Lock className="w-4 h-4 text-zinc-600" />
                      <span className="line-through">Gestión de calendario</span>
                    </li>
                  </ul>
                  {band?.subscription_plan === "pro" && (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/bands/me/subscription`, { plan: "free" }, { headers: getAuthHeaders() });
                          toast.success("PLAN ACTUALIZADO A GARAGE");
                          fetchData();
                        } catch (e) {
                          toast.error("ERROR AL CAMBIAR PLAN");
                        }
                      }}
                      variant="outline"
                      className="w-full btn-ruido border-2 border-zinc-700 font-mono"
                      data-testid="select-free-plan"
                    >
                      CAMBIAR A GARAGE
                    </Button>
                  )}
                </div>

                {/* Pro Plan */}
                <div className={`bg-[#111] border-2 p-6 relative overflow-hidden ${
                  band?.subscription_plan === "pro" ? "border-primary" : "border-zinc-800"
                }`}>
                  <div className="absolute top-0 right-0 bg-primary text-white text-xs font-mono px-4 py-1">
                    RECOMENDADO
                  </div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-black text-white flex items-center gap-2">
                      <Crown className="w-5 h-5 text-primary" />
                      STAGE
                    </h3>
                    {band?.subscription_plan === "pro" && (
                      <span className="bg-primary text-white text-xs font-mono px-2 py-1">ACTUAL</span>
                    )}
                  </div>
                  <p className="text-4xl font-black text-white mb-1">9€<span className="text-sm text-zinc-500 font-normal">/mes</span></p>
                  <p className="text-sm text-green-500 font-mono mb-4">Gratis hasta 01/05/2026</p>
                  <ul className="space-y-3 mb-6">
                    <li className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      Todo lo del plan Garage
                    </li>
                    <li className="flex items-center gap-2 text-white text-sm font-semibold">
                      <Check className="w-4 h-4 text-primary" />
                      Proponer conciertos directamente a bares
                    </li>
                    <li className="flex items-center gap-2 text-white text-sm font-semibold">
                      <Check className="w-4 h-4 text-primary" />
                      Gestión completa de calendario y disponibilidad
                    </li>
                    <li className="flex items-center gap-2 text-white text-sm font-semibold">
                      <Check className="w-4 h-4 text-primary" />
                      Posicionamiento destacado en búsquedas
                    </li>
                    <li className="flex items-center gap-2 text-white text-sm font-semibold">
                      <Check className="w-4 h-4 text-primary" />
                      Soporte prioritario
                    </li>
                  </ul>
                  {band?.subscription_plan !== "pro" && (
                    <Button
                      onClick={async () => {
                        setProcessingPayment(true);
                        try {
                          const res = await axios.post(`${API}/band-subscription/checkout`, {
                            origin_url: window.location.origin
                          }, { headers: getAuthHeaders() });
                          
                          // Redirect to Stripe Checkout
                          window.location.href = res.data.checkout_url;
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "ERROR AL PROCESAR PAGO");
                          setProcessingPayment(false);
                        }
                      }}
                      disabled={processingPayment}
                      className="w-full btn-ruido bg-primary text-white font-mono border-2 border-primary"
                      data-testid="select-pro-plan"
                    >
                      {processingPayment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          PROCESANDO...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          PAGAR 9€/MES
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-zinc-600 font-mono text-xs text-center">
                * Pago seguro con Stripe. Cancela cuando quieras.
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
